import { useEffect, useMemo, useRef, useState } from 'react'
import { Admin } from './screens/Admin'
import { Device } from './screens/Device'
import { Board } from './screens/Board'
import { Cards, type CardView } from './screens/Cards'
import { Score } from './screens/Score'
import { ROSTER } from './data/seed'
import type { Tournament } from './engine/types'
import {
  demote,
  dismissError,
  elevate,
  fetchTournaments,
  login,
  mergedConfirmations,
  mergedScores,
  notice,
  startSync,
  useStore,
  type TournamentSummary,
} from './store'
import { computeAll, outstandingCards, rememberRound, rememberScoreTarget, todayLocal } from './select'
import { Odds } from './screens/Odds'
import { PullToRefresh } from './PullToRefresh'
import { setScreen, startUsage } from './usage'
import { Phones } from './screens/Phones'
import { Bar, InstallPrompt, SyncPill, TabBar } from './ui'

const TOURNAMENT_ID = 'woodsmen-2026'

/**
 * What the server stitched into the head for this link.
 *
 * A shared link is served by a Function that knows which tournament and match
 * it points at, and what palette that tournament wears. All three are facts the
 * app would otherwise have to sign in to learn — which is the wrong order when
 * the sign-in screen is the thing that needs them.
 */
const shellMeta = (name: string): string | null =>
  document.querySelector<HTMLMetaElement>(`meta[name="woodsmen:${name}"]`)?.content || null

/**
 * The theme, before there is a document to read it from.
 *
 * Remembered on the device rather than fetched, because it has to be right on
 * the very first paint — a sign-in screen that renders in the default palette
 * and then flips once the tournament loads is worse than one that waits. A
 * shared link carries the tournament's own theme in the head, which is what
 * makes this correct on a phone that has never opened the app before.
 */
const THEME_KEY = 'woodsmen:theme'
const rememberedTheme = (): string => {
  try {
    return localStorage.getItem(THEME_KEY) || shellMeta('theme') || 'broadcast'
  } catch {
    return shellMeta('theme') || 'broadcast'
  }
}

/**
 * Who appears in the sign-in picker.
 *
 * Alphabetical rather than roster order — the only job here is finding your own
 * name fast. The list is the bundled seed because the live roster lives behind
 * auth, which is a chicken-and-egg on the login screen; it stays correct as long
 * as the field matches the seeded `players_auth` rows, which is the same twelve.
 */
const SIGN_IN_ROSTER = [...ROSTER].sort((a, b) => a.displayName.localeCompare(b.displayName))

function Login() {
  const [tournaments, setTournaments] = useState<TournamentSummary[] | null>(null)
  // A shared link names its own tournament, and it is not always the newest —
  // a link to a match from 2024 has to sign you in to 2024, or it lands on a
  // match id that means something else entirely.
  const [tournamentId, setTournamentId] = useState(shellMeta('tournament') ?? TOURNAMENT_ID)
  const [playerId, setPlayerId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // The roster comes from the server so past years show the field that actually
  // played them, and so a player added this morning can sign in this afternoon.
  useEffect(() => {
    let live = true
    fetchTournaments()
      .then((list) => {
        if (!live || list.length === 0) return
        setTournaments(list)
        // Newest first, so the year in progress is already selected and nobody
        // has to edit a constant here every August. TOURNAMENT_ID is only the
        // bootstrap value for the moment before this list arrives — but a
        // shared link's own year outranks both, or following a link to an old
        // match would quietly sign you in to this year's.
        const linked = shellMeta('tournament')
        setTournamentId(linked && list.some((t) => t.id === linked) ? linked : list[0]!.id)
      })
      .catch(() => live && setTournaments([]))
    return () => {
      live = false
    }
  }, [])

  const chosen = tournaments?.find((t) => t.id === tournamentId)
  const roster = [...(chosen?.players ?? [])].sort((a, b) =>
    a.displayName.localeCompare(b.displayName),
  )

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await login(tournamentId, playerId.trim().toLowerCase())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in')
    } finally {
      setBusy(false)
    }
  }

  /*
   * What this link was for.
   *
   * Most people arriving here tapped something in a group thread rather than
   * opening an app they already knew. Naming the match — or the week — turns a
   * demand for identification into the obvious last step before seeing the
   * thing they were sent, and it is the server's description, so it is the same
   * words that unfurled in the message.
   */
  const opening = shellMeta('match-label')
  const openingWeek = shellMeta('open') === 'week' ? shellMeta('tournament-name') : null

  return (
    <div className="min-h-dvh flex flex-col">
      <Bar kicker="6th Annual · Bandon Dunes" title="The Woodsmen" />
      <form onSubmit={submit} className="p-4 grid gap-4 max-w-sm w-full mx-auto mt-6">
        <img
          src="/crest-lockup.png"
          alt="The Woodsmen"
          className="w-52 mx-auto select-none"
          draggable={false}
        />

        {(opening || openingWeek) && (
          <p className="text-center text-sm leading-snug -mt-1">
            <span className="text-mute">Opening </span>
            <b className="bc-name">{opening ?? openingWeek}</b>
            <span className="text-mute"> — say who you are first.</span>
          </p>
        )}

        {tournaments && tournaments.length > 1 && (
          <label className="grid gap-1">
            {/*
              "Year" was true while every row was a different August. It stopped
              being true the moment a dry run existed alongside the real week,
              and somebody looking for a way to switch tournaments does not
              read a field called Year as the one they want.
            */}
            <span className="bc-label text-mute">Tournament</span>
            <select
              value={tournamentId}
              onChange={(e) => {
                setTournamentId(e.target.value)
                setPlayerId('')
              }}
              className="border-2 border-ink px-3 py-3 bc-name bc-tap bg-paper"
            >
              {tournaments.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="grid gap-1">
          <span className="bc-label text-mute">Your name</span>
          {/*
            A picker, not a text field. Twelve known people sign in on phones in
            a parking lot — typing an exact id is the only way to get this wrong,
            and it is the one step that happens before anyone can ask for help.
          */}
          <select
            value={playerId}
            onChange={(e) => setPlayerId(e.target.value)}
            className="border-2 border-ink px-3 py-3 bc-name bc-tap bg-paper"
          >
            <option value="">
              {tournaments === null ? 'Loading…' : 'Select your name…'}
            </option>
            {(roster.length > 0 ? roster : SIGN_IN_ROSTER).map((p) => (
              <option key={p.id} value={p.id}>
                {p.displayName}
              </option>
            ))}
          </select>
        </label>
        {error && <p className="bg-signal text-paper px-3 py-2 text-xs">{error}</p>}
        <button
          type="submit"
          disabled={busy || !playerId}
          className="bc-tap bg-signal text-paper bc-label py-4 disabled:opacity-40"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  )
}

/**
 * The lock on Setup.
 *
 * Signing in is open because the leaderboard is the point and a code in a car
 * park bought nothing. Setup is different: it holds the draw, the payouts and
 * the button that deletes a week of scoring. The PIN is checked by the server,
 * which mints the admin token — this screen is the visible half of a real lock,
 * not a substitute for one.
 */
/**
 * How long Setup stays open with nobody touching it.
 *
 * The realistic accident is not a stranger: it is one of the twelve wandering
 * into Setup out of curiosity on a phone somebody left unlocked, and finding
 * "Reset the scores". Ten idle minutes closes that window without ever getting
 * in the way of a man who is actually doing the work — every edit pushes it
 * back.
 */
const IDLE_LOCK_MS = 1000 * 60 * 10

/** Is this man on the roster's list of people who may edit? */
function canEdit(doc: Tournament | null, playerId: string | undefined): boolean {
  if (!doc || !playerId) return false
  return doc.players.some((p) => p.id === playerId && p.isAdmin)
}

function SetupGate() {
  const doc = useStore((s) => s.doc)
  const playerId = useStore((s) => s.session?.playerId)
  const [pin, setPin] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const named = canEdit(doc, playerId)

  const unlock = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await elevate(named ? undefined : pin)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not unlock')
      setPin('')
    } finally {
      setBusy(false)
    }
  }

  const editors = (doc?.players ?? []).filter((p) => p.isAdmin).map((p) => p.displayName)

  return (
    <>
      {/*
        A section heading, not a masthead.
        This screen used to BE the Setup tab, so a full Bar with the crest was
        right. It now sits under "This phone" for the nine who are not admins,
        where a second crest and title halfway down reads as a page break rather
        than a section.
      */}
      <div className="flex items-baseline justify-between gap-3 mx-4 mt-5 mb-1 border-t-[3px] border-ink pt-2">
        <h2 className="bc-label">Setup</h2>
        <span className="bc-label text-mute">Locked</span>
      </div>
      <form onSubmit={unlock} className="p-4 grid gap-3">
        <p className="text-[13px] text-mute leading-snug">
          Setup holds the draw, the handicaps, the payouts, the lines and the reset.
          {named
            ? ' It locks itself again after ten minutes.'
            : editors.length > 0
              ? ` ${editors.join(', ')} can open it by tapping below. Anyone else needs the setup PIN.`
              : ' Enter the setup PIN to unlock it on this phone.'}
        </p>
        {!named && (
          <input
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            inputMode="numeric"
            maxLength={8}
            autoFocus
            placeholder="PIN"
            className="border-2 border-ink px-3 py-3 bc-num text-xl tracking-[0.4em] bc-tap bg-paper"
          />
        )}
        {error && <p className="bg-signal text-paper px-3 py-2 text-xs">{error}</p>}
        <button
          type="submit"
          disabled={busy || (!named && pin.length < 3)}
          className="bc-tap bg-signal text-paper bc-label py-4 disabled:opacity-40"
        >
          {busy ? 'Unlocking…' : named ? 'Unlock to edit' : 'Unlock setup'}
        </button>
      </form>
    </>
  )
}

/**
 * The strip that says Setup is open, and shuts it.
 *
 * An unlocked Setup that looks exactly like a locked one is how somebody hands
 * a phone over with the draw one tap from deletion. The countdown is the point:
 * it makes the lock a thing you can see coming rather than a session that
 * expires and throws away what you were typing.
 */
function AdminStrip() {
  const adminToken = useStore((s) => s.adminToken)
  const touchedAt = useStore((s) => s.adminTouchedAt)
  const until = useStore((s) => s.adminUntil)
  const [, tick] = useState(0)

  useEffect(() => {
    if (!adminToken) return
    const id = setInterval(() => tick((n) => n + 1), 1000)
    return () => clearInterval(id)
  }, [adminToken])

  if (!adminToken) return null

  // Whichever runs out first: the idle clock here, or the server's own bound.
  const idleLeft = (touchedAt ?? 0) + IDLE_LOCK_MS - Date.now()
  const hardLeft = (until ?? 0) - Date.now()
  const left = Math.min(idleLeft, hardLeft)
  if (left <= 0) {
    demote()
    return null
  }
  const mins = Math.floor(left / 60000)
  const secs = Math.floor((left % 60000) / 1000)

  return (
    <div className="bg-signal text-paper px-4 py-2 flex items-center justify-between gap-3">
      <span className="bc-label">
        Editing unlocked · locks in {mins}:{String(secs).padStart(2, '0')}
      </span>
      <button type="button" onClick={() => demote()} className="bc-tap bc-label border-2 border-paper px-3 py-1">
        Lock now
      </button>
    </div>
  )
}

/**
 * The match a shared link points at, if this is one.
 *
 * `/m/<matchId>` is served by a Function that injects the preview metadata and
 * then hands over the same single page as `/`. Reading the path back here is
 * what turns the unfurled card into a deep link rather than a nicer front door.
 */
/**
 * You, everywhere.
 *
 * One quiet line riding the top of every screen: place, points, cash.
 * Scott's ask, upgraded from the week view to the whole app — the number a
 * man wants at a glance is the same on the tee, at the board and in the
 * book. Sticky rather than fixed so it scrolls WITH the masthead and only
 * pins once the masthead has gone; hidden entirely until any points settle,
 * because "12th of 12 · 0 pts" before the first card is just weather.
 */
function RankStrip({ doc }: { doc: Tournament }) {
  const scores = useStore(mergedScores)
  const confirmations = useStore(mergedConfirmations)
  const me = useStore((s) => s.session?.playerId ?? '')
  const view = useMemo(
    () => computeAll(doc, scores, confirmations),
    [doc, scores, confirmations],
  )
  const myRow = view.standings.rows.find((r) => r.playerId === me)
  if (!myRow || !view.standings.rows.some((r) => r.points > 0)) return null
  const nth = (n: number): string => {
    const teen = n % 100
    if (teen >= 11 && teen <= 13) return `${n}th`
    return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`
  }
  const flight = view.flights.find((f) => f.playerIds.includes(me))?.flight.name
  return (
    <div className="sticky top-0 z-30 bg-wash border-b border-rule px-4 py-1.5 flex items-baseline justify-between gap-3">
      <span className="bc-label text-mute">
        You — {nth(myRow.position)} of {view.standings.rows.length}
        {flight ? ` · ${flight.replace('Flight ', 'F')}` : ''}
      </span>
      <span className="text-[13px]">
        <span className="bc-num">{myRow.points}</span>
        <span className="text-mute"> pts</span>
        {myRow.cash > 0 && (
          <>
            <span className="text-mute"> · </span>
            <span className="bc-num">${myRow.cash}</span>
          </>
        )}
      </span>
    </div>
  )
}

function sharedMatchId(): string | null {
  const m = /^\/m\/([A-Za-z0-9_-]{1,64})\/?$/.exec(location.pathname)
  return m ? m[1]! : null
}

/**
 * Whether this link was for the week rather than for one match.
 *
 * `/t/<id>` and the bare domain both mean "here is the tournament", and the
 * useful landing for that is the week — tee times, foursomes, who is playing
 * whom — not a scorecard for a round that may not have started.
 */
const sharedWeek = (): boolean => shellMeta('open') === 'week' && !sharedMatchId()

/**
 * Which tournament the link that opened this app was about.
 *
 * The shell injects the tournament it actually resolved, which is stronger than
 * the `?t=` it was asked for — and it is the piece that makes a deep link
 * checkable.
 *
 * Gated on the PATH, and that gate is the load-bearing part. `/` injects the
 * same tag: `functions/index.ts` describes whichever tournament is nearest to
 * today so the bare domain unfurls usefully in the group thread. So the tag alone
 * cannot tell a deep link from the front door, and reading it alone would fire
 * this guard on every plain home-screen launch by anyone signed in to a week that
 * is not the current one — which is every launch on a phone testing the dry run.
 *
 * `/m/<id>` and `/t/<id>` are somebody being SENT somewhere and are the only two
 * paths that make a claim about which week to be in. The bare domain makes none.
 */
export const isDeepLinkPath = (pathname: string): boolean => /^\/(m|t)\/[^/]/.test(pathname)

const linkTournament = (): { id: string; name: string } | null => {
  if (!isDeepLinkPath(location.pathname)) return null
  const id = shellMeta('tournament')
  return id ? { id, name: shellMeta('tournament-name') || 'another week' } : null
}

/**
 * Why a deep link cannot be opened here, or null if it can.
 *
 * Pure and exported so the rule can be tested without a DOM: the effect below
 * only does the setState and the notice.
 */
export function wrongWeek(
  link: { id: string; name: string } | null,
  doc: { id: string; name: string } | null,
): string | null {
  // No link, or no document yet: nothing to disagree about. Note that a link
  // WITHOUT a tournament is not a mismatch — an old link minted before the shell
  // injected one, or a cached shell offline, must still open normally.
  if (!link || !doc || link.id === doc.id) return null
  return `That link was for ${link.name}. You are signed in to ${doc.name}, so it could not be opened.`
}

export default function App() {
  const token = useStore((s) => s.token)
  const doc = useStore((s) => s.doc)
  const online = useStore((s) => s.online)
  // Both have to agree before the app claims to be connected — the browser flag
  // is blind to a wifi that never reaches the internet.
  const reachable = useStore((s) => s.reachable)
  const pending = useStore((s) => s.outbox.length)
  const error = useStore((s) => s.error)
  const isAdmin = useStore((s) => s.session?.isAdmin ?? false)
  const scores = useStore(mergedScores)
  const confirmations = useStore(mergedConfirmations)
  // A shared link decides the opening screen, so it is read once at mount
  // rather than watched — tapping around afterwards must not keep yanking the
  // app back to the match somebody texted an hour ago.
  const [shared] = useState(sharedMatchId)
  /*
   * A shared link's intent lasts until the visitor does something of their own.
   *
   * `/` means "show me the week", and it has to survive the sign-in — but not
   * survive a tap on a leaderboard row, which unmounts and remounts Cards and
   * would otherwise re-open the week instead of the card that was asked for.
   */
  const [openedOnWeek] = useState(sharedWeek)
  /*
   * Which sub-screen the Cards tab is showing.
   *
   * Up here rather than inside `Cards` because it is navigation, and because
   * whoever knows the INTENT has to own it. As local state seeded from a
   * remembered cursor, tapping a birdie in the feed switched to the Cards tab and
   * then opened whatever sub-tab you were last on — so a notification about a
   * birdie landed you on "The week". `onOpenCard` below now says `'cards'`
   * outright, which cannot be outvoted.
   *
   * `App` never unmounts, so this also keeps your place across a trip to another
   * tab, which is what the cursor was doing.
   */
  const [cardView, setCardView] = useState<CardView>(openedOnWeek ? 'preview' : 'cards')
  const [tab, setTab] = useState(shared || openedOnWeek ? 'cards' : 'score')
  /*
   * The usage beacon: opens and per-screen seconds, posted quietly — see
   * usage.ts. Started once there is somebody to attribute them to, told the
   * screen on every tab change.
   */
  useEffect(() => {
    if (token) startUsage()
  }, [token])
  useEffect(() => {
    setScreen(tab)
  }, [tab])
  // Set when a leaderboard row is tapped, so Cards opens on the match you were
  // just looking at rather than on match 1.
  const [cardMatchId, setCardMatchId] = useState<string | null>(shared)
  /*
   * The one square on the card that was asked for.
   *
   * A birdie in the feed names a player and a hole, and landing on a twelve-row
   * card scrolled sideways to the 1st is landing in the right room and not
   * being shown the thing. Kept next to the match id because it is the same
   * gesture: both are set by a tap somewhere else and consumed by Cards.
   */
  const [cardCell, setCardCell] = useState<{ playerId: string; holeNo: number } | null>(null)

  useEffect(() => (token ? startSync() : undefined), [token])

  /*
   * A link from a different week must not open a card from this one.
   *
   * Cards resolves an unknown match id as `view.byMatch[id] ?? first match of
   * the week`, so a deep link from another tournament did not fail — it landed
   * on THIS week's match 1 and presented it as the thing that had been tapped,
   * scrolled to a hole and highlighting a player who is not in that foursome.
   * Two tournaments exist right now (the real week and the dry run) and both
   * number their matches from s0001, so every id collides by construction.
   *
   * Silence would be the same lie one step quieter, so say which week it was
   * for. Fires once: `handled` keeps a later tap of your own from being undone,
   * and the deep link is only cleared while it is still the one the link set.
   */
  const handled = useRef(false)
  const [link] = useState(linkTournament)
  useEffect(() => {
    const wrong = wrongWeek(link, doc)
    if (handled.current || !wrong) return
    handled.current = true
    setCardMatchId((current) => (current === shared ? null : current))
    setCardCell(null)
    setCardView('cards')
    setTab('score')
    notice(wrong)
  }, [doc, link, shared])

  /*
   * The direction is worn by <html>, not by a React tree.
   *
   * Every token lives in CSS, so putting the attribute at the root means the
   * status bar, the overscroll gutter and the safe-area chrome all change with
   * it — which the app's own background would not.
   */
  // Before the document arrives the theme comes from whatever this device saw
  // last, so the sign-in screen wears the tournament's palette instead of
  // flashing the default one at everybody who taps a link.
  const theme = doc?.settings.theme ?? rememberedTheme()
  useEffect(() => {
    const root = document.documentElement
    if (theme === 'broadcast') root.removeAttribute('data-theme')
    else root.setAttribute('data-theme', theme)
    try {
      localStorage.setItem(THEME_KEY, theme)
    } catch {
      // A full quota costs the next first paint its color, and nothing else.
    }
  }, [theme])

  /*
   * Is there a card from a previous day that nobody signed for?
   *
   * Marks the Board tab, because the nudge itself lives in the feed and a notice
   * two taps down is a notice for whoever was already going there. Only counts
   * cards that have outlived their own day — during the round an unsigned card is
   * a round in progress, and a dot that is lit every afternoon is a dot nobody
   * sees by Wednesday.
   */
  const needsSigning =
    !!doc && outstandingCards(doc, scores, confirmations, todayLocal()).some((c) => c.daysLate > 0)

  if (!token) return <Login />

  return (
    <div className="min-h-dvh flex flex-col">
      <PullToRefresh />
      <InstallPrompt />
      <SyncPill
        online={online && reachable}
        pending={pending}
        error={error}
        onDismiss={dismissError}
      />
      <main className="flex-1 pb-tabbar">
        {doc && <RankStrip doc={doc} />}
        {!doc ? (
          <>
            <Bar title="The Woodsmen" />
            <p className="px-4 py-10 text-center text-mute text-sm">
              {online ? 'Loading the tournament…' : 'Offline — nothing saved on this phone yet.'}
            </p>
          </>
        ) : tab === 'score' ? (
          <Score doc={doc} />
        ) : tab === 'board' ? (
          <Board
            doc={doc}
            onOpenCard={(matchId, cell) => {
              setCardMatchId(matchId)
              setCardCell(cell ?? null)
              // The card, not wherever this tab was left. This is the whole fix.
              setCardView('cards')
              setTab('cards')
            }}
            /*
             * Signing lives on the Score tab, so an unsigned-card nudge has to
             * be able to send you there — and to the RIGHT round, which is the
             * whole point: the card being chased is yesterday's, and Score
             * defaults to today's. `rememberRound` is the same cursor Score
             * reads on mount, so setting it before the switch is what makes the
             * tab open where it was asked to rather than where it left off.
             */
            onSign={(roundId) => {
              rememberRound(roundId)
              setTab('score')
            }}
          />
        ) : tab === 'cards' ? (
          <Cards
            doc={doc}
            matchId={cardMatchId}
            onMatch={setCardMatchId}
            focusCell={cardCell}
            section={cardView}
            onSection={setCardView}
            /* The same jump the Board's feed offers, from the week view. */
            onSign={(roundId) => {
              rememberRound(roundId)
              setTab('score')
            }}
            /* A tapped cell is an errand: land the scorer on that exact hole. */
            onFix={(roundId, groupId, holeNo) => {
              rememberRound(roundId)
              rememberScoreTarget({ groupId, holeNo })
              setTab('score')
            }}
          />
        ) : tab === 'odds' ? (
          <Odds doc={doc} />
        ) : isAdmin ? (
          <>
            <AdminStrip />
            <Admin doc={doc} />
            <Device doc={doc} />
            <Phones doc={doc} />
          </>
        ) : (
          <>
            {/*
              This phone first, the locked setup below it.
              For eleven of the twelve this tab is not "Setup" at all — it is
              where you fix having picked the wrong name, send scores that are
              stuck, and turn on birdie alerts. All of that used to sit under a
              PIN form headed "Locked", which reads as a door rather than a
              scroll, so nobody went past it.
            */}
            <Bar title="This phone" sub="Who you are, alerts, and this build" />
            <Device doc={doc} />
            {/* Renders only for roster admins — Cramer's field-health screen. */}
            <Phones doc={doc} />
            <SetupGate />
          </>
        )}
      </main>
      <TabBar tab={tab} onTab={setTab} flag={needsSigning ? 'board' : null} />
    </div>
  )
}
